"use client";

import { useState } from "react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/logo";

const schema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  company: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export default function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name.trim(),
          email: data.email.trim(),
          company: data.company?.trim() || undefined,
          source: "register",
        }),
      });
      const json = await res.json().catch(() => ({})) as { ok?: boolean; alreadyJoined?: boolean; error?: string };
      if (json.alreadyJoined) {
        setAlreadyJoined(true);
        setJoined(true);
      } else if (json.ok) {
        setJoined(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="mb-8 text-center">
        <Link href="/" className="inline-flex items-center justify-center mb-6">
          <Logo size="lg" showText={false} />
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Request early access</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Axlynq is currently in private beta. Join the waitlist and we&apos;ll reach out when a spot opens.
        </p>
      </div>

      {joined ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
            <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold mb-2">
            {alreadyJoined ? "You're already on the list!" : "You're on the waitlist!"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {alreadyJoined
              ? "We already have your email. We'll notify you when your spot opens up."
              : "Thanks for your interest. We'll notify you as soon as your spot is ready."}
          </p>
          <div className="mt-6 pt-6 border-t border-border">
            <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Back to home
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-card p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" placeholder="Jane Smith" {...register("name")} className="h-11" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" placeholder="you@company.com" {...register("email")} className="h-11" />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="company" placeholder="Acme Inc." {...register("company")} className="h-11" />
              </div>
              <Button type="submit" className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Request access
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have access?{" "}
            <Link href="/login" className="font-medium text-foreground hover:underline">
              Sign in
            </Link>
          </p>
        </>
      )}
    </div>
  );
}
